import ReactTable from '../../../components/shared/ReactTable';
import { COLUMNS } from '../../../constants/tableColumns';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useGetModemsQuery } from '../../../services/Modems/ModemsService';
import ModemAccordion from './ModemAccordion';
import { Link, NoContent } from '../../../components/shared';
import { routeConstants } from '../../../constants/routeConstants';
import FIELD_TYPE from '../../../constants/fieldType';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { getDateFormat } from '../../../utils/common-methods';
import { setModemsFilters } from '../../../redux-slice/modemFilters';
import { useDispatch } from 'react-redux';
import { Button } from '@mui/material';
import Modal from '../../../components/shared/Modal';
import ModemHistoryModel from '../ModemHistoryModel/ModemHistoryModel';
const { NOT_APPLICABLE } = FIELD_TYPE;

export default function ModemsTable({ onTotalModem, searchParams, modems }) {
  const [currentPage, setCurrentPage] = useState(modems?.pageNumber);
  const [perPageNumber, setPerPageNumber] = useState(modems?.rowsPerPage);
  const [totalCount, setTotalCount] = useState(0);
  const [searchFilterData, setSearchFilterData] = useState(searchParams);
  const [skipCall, setSkipCall] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [selectedModemId, setSelectedModemId] = useState(null);
  const dispatch = useDispatch();
  const handleOpenHistoryModal = useCallback((modemId) => {
    setSelectedModemId(modemId);
    setHistoryModal(true);
  }, []);

  const handleCloseHistoryModal = useCallback(() => {
    setHistoryModal(false);
    setSelectedModemId(null);
  }, []);

  // Custom history column to open modal
  const HISTORY_COLUMN = useMemo(
    () => ({
      Header: () => null,
      id: 'history',
      width: 80,
      isPadding: true,
      padding: '20px 32px 20px 0px',
      Cell: ({ row }) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleOpenHistoryModal(row.original.modemId);
          }}
          aria-label={`Show history for ${row.original.serialNumber || 'modem'}`}
          size="small"
          color="primary"
          sx={{
            minWidth: 'auto',
            padding: '4px',
            '& .MuiButton-startIcon': {
              marginRight: 0,
            },
          }}
        >
          History
        </Button>
      ),
    }),
    [handleOpenHistoryModal],
  );

  const modemColumns = useMemo(
    () => [COLUMNS.EXPAND_ICON_COLUMN, ...COLUMNS.MODEMS_TABLE, HISTORY_COLUMN],
    [HISTORY_COLUMN],
  );

  const MODEMS_COLUMNS = useMemo(() => modemColumns, [modemColumns]);

  const {
    data: latestData,
    isError,
    isSuccess,
    isLoading,
    error,
    isFetching,
  } = useGetModemsQuery(
    {
      searchParams: searchFilterData,
      page: currentPage,
      perPage: perPageNumber,
    },
    { skip: skipCall },
  );

  const [tableData, setTableData] = useState([]);

  const serialNumberLink = useCallback(
    (id, name) => (
      <Link underline="none" to={`/${routeConstants.MODEMS_ROUTE}/${id}`}>
        {name}
      </Link>
    ),
    [],
  );

  useEffect(() => {
    if (isSuccess && latestData) {
      const data = latestData?.modems?.map((modem) => {
        // Normalize ID field: API returns 'id' but code expects 'modemId'
        const modemId = modem?.id || modem?.modemId;
        const transformModem = { ...modem, modemId };
        transformModem.serialNumber = serialNumberLink(modemId, modem?.serialNumber);
        transformModem.assetId = modem?.assetId ?? NOT_APPLICABLE;
        transformModem.installedDate = transformModem?.installedDate
          ? getDateFormat(transformModem?.installedDate)
          : NOT_APPLICABLE;
        transformModem.inEffectFrom = transformModem?.inEffectFrom
          ? getDateFormat(transformModem?.inEffectFrom)
          : NOT_APPLICABLE;
        transformModem.inEffectTo = transformModem?.inEffectTo
          ? getDateFormat(transformModem?.inEffectTo)
          : NOT_APPLICABLE;
        return transformModem;
      });
      setTableData(data);
      let total = latestData?.pagination?.total ?? 0;
      setTotalCount(total);
      onTotalModem(total ?? totalCount);
    }
  }, [isSuccess, latestData]);

  useEffect(() => {
    setSkipCall(true);
    setSearchFilterData(searchParams);
    setCurrentPage(modems?.pageNumber);
    setPerPageNumber(modems?.rowsPerPage);
    setSkipCall(false);
  }, [searchParams]);

  const pageChangeHandler = (currentPage) => {
    dispatch(setModemsFilters({ ...modems, pageNumber: currentPage, rowsPerPage: perPageNumber }));
    setCurrentPage(currentPage);
  };

  const perPageChangeHandler = (newPerPage) => {
    dispatch(setModemsFilters({ ...modems, rowsPerPage: newPerPage, pageNumber: currentPage }));
    setPerPageNumber(newPerPage);
  };

  const detailModemComponent = ({ row }) => {
    const original = row?.original;
    return original && <ModemAccordion modemInfo={original} />;
  };

  return (
    <>
      {(isLoading || isFetching) && <LoadingSpinner containerHeight={300} />}
      {isError && (
        <NoContent
          title="No Modem Found"
          desc={(isError && error?.data?.message) || <>There are no modem present with this filter.</>}
        />
      )}
      {!isLoading && !isFetching && !isError && tableData && (
        <ReactTable
          data={tableData}
          columns={MODEMS_COLUMNS}
          pagination
          manualPagination
          totalCount={totalCount}
          pageNumberForTablePagination={currentPage - 1}
          onPageChange={pageChangeHandler}
          onPerPageChange={perPageChangeHandler}
          perPage={perPageNumber}
          expandable
          renderRows
          renderRowSubComponent={detailModemComponent}
        />
      )}

      {/* History Modal */}
      {historyModal && (
        <Modal
          open={historyModal}
          onClose={handleCloseHistoryModal}
          title={'Modem History'}
          titleVariant="subtitle10"
          closeIcon={true}
          PaperProps={{
            sx: {
              width: '90%',
              maxWidth: 'none',
              maxHeight: '90%',
              height: '70%',
              overflow: 'hidden',
            },
          }}
        >
          <ModemHistoryModel modemId={selectedModemId} />
        </Modal>
      )}
    </>
  );
}
